import type { CurrentUserClientsVariables, UserReactionAccentColor } from '@ms/yammer-graphql';
import queryCurrentUser from '@ms/yammer-graphql/dist/operations/queryCurrentUser.graphql';
import { reportError } from '@ms/yammer-telemetry';
import type { RequestInitiator } from '@ms/yammer-telemetry-support';

import type { GraphqlApiRequest, ManagedApiCaching, ManagedApiLifecycle, ManagedApiRequest } from '../..';
import { getGraphqlApiErrorReason, getGraphqlApiRequest, managedApiAction } from '../..';
import { normalizeCurrentUser } from '../../normalization/graphql/normalizeCurrentUser';
import type { NormalizedCurrentUserEntities, NormalizedUserPreferences } from '../../normalization/types';
import type { User } from '../../state/types';
import type { GenericThunkAction, PromiseThunkAction } from '../../types';
import { getApiHost } from '../session/selectors';

import type {
  CurrentUserResponse,
  SessionActivatePendingUserFulfilledAction,
  SessionActivatePendingUserPendingAction,
  SessionActivatePendingUserRejectedAction,
  SessionLoadCurrentUserFulfilledAction,
  SessionLoadCurrentUserPendingAction,
  SessionLoadCurrentUserRejectedAction,
} from './actions.types';
import { getCacheKey } from './cacheKey';

const loadCurrentUserPending = (): SessionLoadCurrentUserPendingAction => ({
  type: 'SESSION.LOAD_CURRENT_USER_PENDING',
});

const loadCurrentUserFulfilled = (
  id: User['id'],
  entities: NormalizedCurrentUserEntities,
  locale: string,
  isGuest: boolean,
  isNetworkAdmin: boolean,
  isVerifiedNetworkAdmin: boolean,
  isRestrictedToViewOnlyMode: boolean,
  userPreferences: NormalizedUserPreferences,
  isUserForceOptedIn: boolean,
  canScheduleAndCreateTeamsMeeting: boolean,
  canSendOnBehalfOf: boolean,
  directMessageThreadStarterSmallFileUploadUrl: string,
  userReactionAccentColor: UserReactionAccentColor
): SessionLoadCurrentUserFulfilledAction => ({
  type: 'SESSION.LOAD_CURRENT_USER_FULFILLED',
  payload: {
    id,
    entities,
    locale,
    isGuest,
    isNetworkAdmin,
    isVerifiedNetworkAdmin,
    isRestrictedToViewOnlyMode,
    userPreferences,
    isUserForceOptedIn,
    canScheduleAndCreateTeamsMeeting,
    canSendOnBehalfOf,
    directMessageThreadStarterSmallFileUploadUrl,
    userReactionAccentColor,
  },
});

const loadCurrentUserRejected = (error: unknown): SessionLoadCurrentUserRejectedAction => ({
  type: 'SESSION.LOAD_CURRENT_USER_REJECTED',
  payload: {
    error,
  },
  error: true,
});

export interface ActivatePendingUserResponse {
  readonly ok?: string;
}

const activatePendingUserFulfilled = (): SessionActivatePendingUserFulfilledAction => ({
  type: 'SESSION.ACTIVATE_PENDING_USER_FULFILLED',
});

const activatePendingUserPending = (): SessionActivatePendingUserPendingAction => ({
  type: 'SESSION.ACTIVATE_PENDING_USER_PENDING',
});

const activatePendingUserRejected = (): SessionActivatePendingUserRejectedAction => ({
  type: 'SESSION.ACTIVATE_PENDING_USER_REJECTED',
});

type ActivatePendingUser = () => GenericThunkAction<void>;
const activatePendingUser: ActivatePendingUser = () => async (dispatch, getState) => {
  const state = getState();
  const apiHost = getApiHost(state);

  try {
    const request: ManagedApiRequest = {
      apiName: 'ActivatePendingUser',
      method: 'POST',
      host: apiHost,
      requestType: 'LegacyYammer',
      path: '/api/v1/users/current/activate.json',
      mode: 'cors',
    };
    const lifecycle: ManagedApiLifecycle<ActivatePendingUserResponse> = {
      pending: activatePendingUserPending,
      fulfilled: activatePendingUserFulfilled,
      rejected: activatePendingUserRejected,
    };

    await dispatch(managedApiAction({ request, lifecycle }));
  } catch (error) {
    reportError({ error });
  }
};

export type LoadCurrentUserWithIsLegacyExternalNetwork = (caching?: ManagedApiCaching) => PromiseThunkAction<void>;
export const loadCurrentUserWithIsLegacyExternalNetwork: LoadCurrentUserWithIsLegacyExternalNetwork =
  (caching) => async (dispatch) => {
    const request: GraphqlApiRequest<CurrentUserClientsVariables> = getGraphqlApiRequest({
      body: {
        operationName: 'CurrentUserClients',
        query: queryCurrentUser,
        variables: { includeIsLegacyExternalNetwork: true },
      },
    });
    await dispatch(load(request, caching));
  };

export type LoadCurrentUserRequest = (initiator?: RequestInitiator) => GraphqlApiRequest<CurrentUserClientsVariables>;
export const loadCurrentUserRequest: LoadCurrentUserRequest = (initiator) =>
  getGraphqlApiRequest({
    body: {
      operationName: 'CurrentUserClients',
      query: queryCurrentUser,
    },
    headers: {
      'X-App-Operation-Priority': 'high',
    },
    initiator,
  });

interface LoadCurrentUserOptions {
  readonly caching?: ManagedApiCaching;
  readonly initiator?: RequestInitiator;
}
type LoadCurrentUser = (options?: LoadCurrentUserOptions) => PromiseThunkAction<void>;
export const loadCurrentUser: LoadCurrentUser =
  ({ caching, initiator } = {}) =>
  async (dispatch) => {
    const request = loadCurrentUserRequest(initiator);

    await dispatch(load(request, caching));
  };

type Load = (
  request: GraphqlApiRequest<CurrentUserClientsVariables>,
  caching?: ManagedApiCaching
) => PromiseThunkAction<void>;
const load: Load = (request, caching) => async (dispatch) => {
  const lifecycle: ManagedApiLifecycle<CurrentUserResponse> = {
    pending: loadCurrentUserPending,
    fulfilled: (rawCurrentUser) => {
      const {
        entities,
        result,
        locale,
        isGuest,
        isNetworkAdmin,
        isVerifiedNetworkAdmin,
        isRestrictedToViewOnlyMode,
        userPreferences,
        isUserForceOptedIn,
        canScheduleAndCreateTeamsMeeting,
        canSendOnBehalfOf,
        directMessageThreadStarterSmallFileUploadUrl,
        reactionAccentColor,
      } = normalizeCurrentUser(rawCurrentUser);

      return loadCurrentUserFulfilled(
        result,
        entities,
        locale,
        isGuest,
        isNetworkAdmin,
        isVerifiedNetworkAdmin,
        isRestrictedToViewOnlyMode,
        userPreferences,
        isUserForceOptedIn,
        canScheduleAndCreateTeamsMeeting,
        canSendOnBehalfOf,
        directMessageThreadStarterSmallFileUploadUrl,
        reactionAccentColor
      );
    },
    rejected: (error) => {
      const reason = getGraphqlApiErrorReason(error);

      if (reason === 'PENDING_USER' || reason === 'USER_NOT_MAPPED') {
        dispatch(activatePendingUser());
      }

      return loadCurrentUserRejected(error);
    },
  };

  const cachingWithKey = caching && {
    ...caching,
    key: getCacheKey(),
  };
  await dispatch(managedApiAction({ request, lifecycle, caching: cachingWithKey }));
};
